/**
 * Roles del módulo EMCALI — permisos base por rol.
 * Permisos **adicionales** (más allá de este catálogo): solo el rol administrador puede configurarlos.
 */

export type ProyectosRolId =
  | "administrador"
  | "jefe"
  | "factibilidad"
  | "disenador"
  | "liquidacion_diseno"
  | "supervisor"
  | "operacion"
  | "mantenimiento"
  | "laboratorio"
  | "control_energia";

export type ProyectosRolDef = {
  id: ProyectosRolId;
  rolDb: string;
  label: string;
  descripcion: string;
  menuRoutes: string[];
  /** 0=Solicitud, 1=Expediente (todos), 2=Diseño, 3=Supervisión, 4=Ejecución, 5=Puesta */
  gestionTabs: number[];
  puedeGestionarUsuarios: boolean;
  puedeCrearProyecto: boolean;
  /** Recibe en su correo (usuario.correo) notificaciones internas de jefe */
  recibeCorreosJefe: boolean;
  /** Puede abrir pantalla de permisos adicionales por usuario */
  puedeConfigurarPermisosAdicionales: boolean;
};

const MENU = {
  dashboard: "/dashboard",
  panel: "/proyectos-panel",
  nuevo: "/proyectos-panel/nuevo",
  burden: "/calculo-burden",
  usuarios: "/usuarios",
  permisos: "/proyectos-permisos",
  asignacion: "/proyectos-asignacion-disenador",
  redisenno: "/proyectos-redisenno",
  asignacionInterventor: "/proyectos-asignacion-interventor",
} as const;

export const PROYECTOS_ROLES: ProyectosRolDef[] = [
  {
    id: "administrador",
    rolDb: "administrador",
    label: "Administrador",
    descripcion:
      "Control total del módulo. Único rol que puede otorgar permisos adicionales a otros usuarios.",
    menuRoutes: Object.values(MENU),
    gestionTabs: [0, 1, 2, 3, 4, 5],
    puedeGestionarUsuarios: true,
    puedeCrearProyecto: true,
    recibeCorreosJefe: false,
    puedeConfigurarPermisosAdicionales: true,
  },
  {
    id: "jefe",
    rolDb: "jefe",
    label: "Jefe de unidad",
    descripcion:
      "Asignación de diseñador, aprobación de rediseños y asignación de interventor. Recibe correos internos en su cuenta.",
    menuRoutes: [MENU.dashboard, MENU.panel, MENU.asignacion, MENU.redisenno, MENU.asignacionInterventor],
    gestionTabs: [0, 2, 3, 4, 5],
    puedeGestionarUsuarios: false,
    puedeCrearProyecto: false,
    recibeCorreosJefe: true,
    puedeConfigurarPermisosAdicionales: false,
  },
  {
    id: "factibilidad",
    rolDb: "factibilidad",
    label: "Factibilidad",
    descripcion: "Solicitud del servicio: revisión de datos y documentación (cédula / uso especial).",
    menuRoutes: [MENU.dashboard, MENU.panel],
    gestionTabs: [0],
    puedeGestionarUsuarios: false,
    puedeCrearProyecto: false,
    recibeCorreosJefe: false,
    puedeConfigurarPermisosAdicionales: false,
  },
  {
    id: "disenador",
    rolDb: "disenador",
    label: "Diseñador",
    descripcion: "Etapa de diseño en proyectos asignados por el jefe.",
    menuRoutes: [MENU.dashboard, MENU.panel, MENU.burden],
    gestionTabs: [2],
    puedeGestionarUsuarios: false,
    puedeCrearProyecto: false,
    recibeCorreosJefe: false,
    puedeConfigurarPermisosAdicionales: false,
  },
  {
    id: "liquidacion_diseno",
    rolDb: "liquidacion_diseno",
    label: "Liquidación de diseño",
    descripcion:
      "Carga la liquidación de diseño tras la aprobación del diseño y habilita el pago del solicitante.",
    menuRoutes: [MENU.dashboard, MENU.panel],
    gestionTabs: [2],
    puedeGestionarUsuarios: false,
    puedeCrearProyecto: false,
    recibeCorreosJefe: false,
    puedeConfigurarPermisosAdicionales: false,
  },
  {
    id: "supervisor",
    rolDb: "supervisor",
    label: "Supervisor",
    descripcion: "Supervisión y aprobación (etapa 3 del flujo). Alcance detallado por definir.",
    menuRoutes: [MENU.dashboard, MENU.panel],
    gestionTabs: [3],
    puedeGestionarUsuarios: false,
    puedeCrearProyecto: false,
    recibeCorreosJefe: false,
    puedeConfigurarPermisosAdicionales: false,
  },
  {
    id: "operacion",
    rolDb: "operacion",
    label: "Operación",
    descripcion: "Ejecución de obras. Alcance detallado por definir.",
    menuRoutes: [MENU.dashboard, MENU.panel],
    gestionTabs: [4],
    puedeGestionarUsuarios: false,
    puedeCrearProyecto: false,
    recibeCorreosJefe: false,
    puedeConfigurarPermisosAdicionales: false,
  },
  {
    id: "mantenimiento",
    rolDb: "mantenimiento",
    label: "Mantenimiento",
    descripcion: "Ejecución de obras (unidad mantenimiento). Alcance detallado por definir.",
    menuRoutes: [MENU.dashboard, MENU.panel],
    gestionTabs: [4],
    puedeGestionarUsuarios: false,
    puedeCrearProyecto: false,
    recibeCorreosJefe: false,
    puedeConfigurarPermisosAdicionales: false,
  },
  {
    id: "laboratorio",
    rolDb: "laboratorio",
    label: "Laboratorio",
    descripcion: "Cálculo de burden y apoyos de laboratorio. Alcance detallado por definir.",
    menuRoutes: [MENU.dashboard, MENU.burden],
    gestionTabs: [],
    puedeGestionarUsuarios: false,
    puedeCrearProyecto: false,
    recibeCorreosJefe: false,
    puedeConfigurarPermisosAdicionales: false,
  },
  {
    id: "control_energia",
    rolDb: "control_energia",
    label: "Control de energía",
    descripcion: "Puesta en servicio. Alcance detallado por definir.",
    menuRoutes: [MENU.dashboard, MENU.panel],
    gestionTabs: [5],
    puedeGestionarUsuarios: false,
    puedeCrearProyecto: false,
    recibeCorreosJefe: false,
    puedeConfigurarPermisosAdicionales: false,
  },
];

/** Roles mostrados al crear usuario (9 roles + compatibilidad operación legacy). */
export const CARGOS_SELECT_CANONICAL = [
  { id: 1, nombre: "ADMINISTRADOR", rolDb: "administrador" as const },
  { id: 20, nombre: "jefe", rolDb: "jefe" as const },
  { id: 19, nombre: "factibilidad", rolDb: "factibilidad" as const },
  { id: 21, nombre: "disenador", rolDb: "disenador" as const },
  { id: 27, nombre: "liquidacion_diseno", rolDb: "liquidacion_diseno" as const },
  { id: 22, nombre: "supervisor", rolDb: "supervisor" as const },
  { id: 23, nombre: "operacion", rolDb: "operacion" as const },
  { id: 24, nombre: "mantenimiento", rolDb: "mantenimiento" as const },
  { id: 25, nombre: "laboratorio", rolDb: "laboratorio" as const },
  { id: 26, nombre: "control_energia", rolDb: "control_energia" as const },
];

export const CARGOS_SELECT_ROLES = [
  { id: 1, nombre: "ADMINISTRADOR", rolDb: "administrador" as const },
  { id: 20, nombre: "jefe", rolDb: "jefe" as const },
  { id: 19, nombre: "factibilidad", rolDb: "factibilidad" as const },
  { id: 21, nombre: "disenador", rolDb: "disenador" as const },
  { id: 27, nombre: "liquidacion_diseno", rolDb: "liquidacion_diseno" as const },
  { id: 22, nombre: "supervisor", rolDb: "supervisor" as const },
  { id: 23, nombre: "operacion", rolDb: "operacion" as const },
  { id: 24, nombre: "mantenimiento", rolDb: "mantenimiento" as const },
  { id: 25, nombre: "laboratorio", rolDb: "laboratorio" as const },
  { id: 26, nombre: "control_energia", rolDb: "control_energia" as const },
  { id: 2, nombre: "proyectos", rolDb: "operacion" as const },
  { id: 18, nombre: "proyetos", rolDb: "operacion" as const },
];

export function findRolDefByDb(rolDb: string): ProyectosRolDef | undefined {
  let n = String(rolDb || "").toLowerCase().trim();
  if (n === "proyectos" || n === "proyetos") n = "operacion";
  return PROYECTOS_ROLES.find((r) => r.rolDb === n);
}

export function permisosCamposPorRol(rolId: ProyectosRolId): Record<string, string[]> {
  const map: Record<ProyectosRolId, Record<string, string[]>> = {
    administrador: {
      solicitud_servicio: ["*"],
      factibilidad_servicio: ["*"],
      revision_aprobacion: ["*"],
      ejecucion_obras: ["*"],
      puesta_servicio: ["*"],
    },
    factibilidad: {
      solicitud_servicio: [
        "revision_observaciones_solicitud",
        "documentos_solicitud",
        "acciones_aprobacion_solicitud",
        "revision_documental_factibilidad",
      ],
      factibilidad_servicio: ["revision_documental_factibilidad", "gestion_documental"],
      revision_aprobacion: [],
      ejecucion_obras: [],
      puesta_servicio: [],
    },
    jefe: {
      solicitud_servicio: ["acciones_aprobacion_solicitud"],
      factibilidad_servicio: ["revision_documental_factibilidad", "gestion_documental"],
      revision_aprobacion: ["decision_supervision"],
      ejecucion_obras: [],
      puesta_servicio: [],
    },
    disenador: {
      solicitud_servicio: [],
      factibilidad_servicio: ["*"],
      revision_aprobacion: ["plano_diseno", "presupuesto_diseno", "decision_revision_diseno"],
      ejecucion_obras: [],
      puesta_servicio: [],
    },
    liquidacion_diseno: {
      solicitud_servicio: [],
      factibilidad_servicio: ["liquidacion_diseno"],
      revision_aprobacion: [],
      ejecucion_obras: [],
      puesta_servicio: [],
    },
    supervisor: {
      solicitud_servicio: [],
      factibilidad_servicio: [],
      revision_aprobacion: ["*", "informe_avance_obra"],
      ejecucion_obras: [],
      puesta_servicio: [],
    },
    operacion: {
      solicitud_servicio: [],
      factibilidad_servicio: [],
      revision_aprobacion: [],
      ejecucion_obras: ["*"],
      puesta_servicio: [],
    },
    mantenimiento: {
      solicitud_servicio: [],
      factibilidad_servicio: [],
      revision_aprobacion: [],
      ejecucion_obras: ["*"],
      puesta_servicio: [],
    },
    laboratorio: {
      solicitud_servicio: [],
      factibilidad_servicio: [],
      revision_aprobacion: [],
      ejecucion_obras: [],
      puesta_servicio: [],
    },
    control_energia: {
      solicitud_servicio: [],
      factibilidad_servicio: [],
      revision_aprobacion: [],
      ejecucion_obras: [],
      puesta_servicio: ["*"],
    },
  };
  return map[rolId] || map.operacion;
}

export function permisosFlagsPorRol(rolId: ProyectosRolId) {
  const def = PROYECTOS_ROLES.find((r) => r.id === rolId);
  const admin = rolId === "administrador";
  const gestiona =
    admin ||
    (def
      ? [
          "jefe",
          "factibilidad",
          "disenador",
          "liquidacion_diseno",
          "supervisor",
          "operacion",
          "mantenimiento",
          "control_energia",
        ].includes(rolId)
      : false);
  return {
    puede_crear: admin,
    puede_ver_index_completo: gestiona || rolId === "laboratorio",
    puede_gestionar: gestiona,
    puede_editar: gestiona,
    puede_eliminar: admin,
    puede_configurar_permisos_adicionales: admin,
  };
}
